import {Component, Input, ViewEncapsulation, Output, EventEmitter, OnInit, OnDestroy, ChangeDetectionStrategy} from '@angular/core';
import {Observable, of, Subject} from 'rxjs';
import {debounceTime, distinctUntilChanged, map, startWith, switchMap, takeWhile} from 'rxjs/operators';

import {OrderItemDelivery} from '../../../../models/order-item-delivery.model';
import {OrderItem} from '../../../../models/order-item.model';
import {ProductService} from '../../../../services/product.service';
import {
  AllocationGroup,
  AllocationQty,
  InventoryAllocation,
  InventoryAllocationDelivery
} from '../../../../interfaces/allocation-group';
import {isEmptyArray} from '../../../../shared/utils';


@Component({
    selector: 'app-sku-inventory-allocation-control',
    templateUrl: './sku-inventory-allocation-control.component.html',
    styleUrls: ['./sku-inventory-allocation-control.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false,
    changeDetection: ChangeDetectionStrategy.Eager
})
export class SkuInventoryAllocationControlComponent implements  OnInit, OnDestroy {

  @Input() orderItem: OrderItem;
  @Input() readonly = false;
  @Input() disabled = false;
  @Input() skuId: number;

  private _delivery: OrderItemDelivery;

  @Output() onSelectAllocationGroup =  new EventEmitter<InventoryAllocationDelivery>();

  searchSubject: Subject<string> = new Subject<string>();
  filteredAllocationGroups$: Observable<AllocationQty[]>;
  selectedInventoryAllocationId: number;

  inventoryAllocation$: Observable<InventoryAllocation>;
  someVariationsHasAllocationGroups$: Observable<boolean>;
  variationHasAllocationGroups$: Observable<boolean>;
  isOutOfStock$: Observable<boolean>;
  fetchingAllocationInventory$: Observable<boolean>;

  private isAlive = true;


  @Input() set delivery(val: OrderItemDelivery) {
    this._delivery = val;
    this.setSelectedInventoryAllocation();
  }

  get delivery(): OrderItemDelivery {
    return this._delivery;
  }

  constructor(private productService: ProductService) {
  }

  ngOnInit() {

    const productId = this.orderItem?.product?.id;
    this.inventoryAllocation$ = this.getInventoryAllocation$();
    this.someVariationsHasAllocationGroups$ = this.productService.someVariationsHasAllocationGroups(productId);
    this.variationHasAllocationGroups$ = this.productService.variationsHasInventoryAllocations(productId, this.skuId);
    this.isOutOfStock$ = this.productService.isInventoryAllocationOutOfStock(productId, this.skuId);
    this.fetchingAllocationInventory$ = this.productService.isFetchingProductAllocations$(productId);

    // search by label in dropdown
    this.filteredAllocationGroups$ = this.searchSubject
      .pipe(
        takeWhile(() => this.isAlive),
        debounceTime(200),
        distinctUntilChanged(),
        startWith(''),
        switchMap(term => {

          return this.inventoryAllocation$.pipe(
            map(inventoryAllocation => {
                if (!inventoryAllocation) {
                  return [];
                }

                if (!term) {
                  // return all
                  return inventoryAllocation.allocation_qty || [];
                }

                // filter by term
                return (inventoryAllocation.allocation_qty || []).filter(g =>
                  g.allocation_group_label.toLowerCase().includes(term.toLowerCase())
                ) || [];
            })
          );
        })
      );
  }

  ngOnDestroy() {
    this.isAlive = false;
  }


  get enabled(): boolean {
    return this.orderItem.product.isEnabledAllocatedInventory;
  }

  protected onSelect(group: AllocationGroup) {
    if (!group) {
      return;
    }
    this.onSelectAllocationGroup.emit({
      sku_id: this.skuId,
      allocation_group_id: group.allocation_group_id,
      allocation_group_label: group.allocation_group_label,
      quantity: 0
    });
  }



  get isBuyingWindow(): boolean {
    return this.orderItem?.product.isBuyingWindow;
  }


  get someDeliveriesHaveAllocationGroup(): boolean {

    if (this.orderItem?.product.isBuyingWindow === true) {
      // this should render only on-demand products
      return false;
    }

    if (!this.readonly) {
      return this.orderItem?.product.isEnabledAllocatedInventory;
    }   else {
      return this.orderItem?.product.isEnabledAllocatedInventory;
    }
  }

  getInventoryAllocation$(): Observable<InventoryAllocation> {
    if (!this.enabled) {
      return of(null);
    }
    return this.productService.getInventoryAllocationForSku(this.orderItem?.product?.id, this.skuId);
  }

  get allocationGroupWithLoading$(): Observable<{ loading: boolean; value: AllocationGroup | null }> {
    if (!this.delivery?.allocation_group_id) {
      return of({ loading: false, value: null });
    }

    return this.productService.getAllocationGroupById(this.delivery?.allocation_group_id).pipe(
      map(value => ({ loading: false, value })),
      startWith({ loading: true, value: null })
    );
  }

  get hasSelectedAllocationGroup(): boolean {
    return !!this.delivery?.allocation_group_id;

  }

  private setSelectedInventoryAllocation() {
    if (!this.delivery || !this.skuId) {
      return;
    }
    if (isEmptyArray(this.delivery.allocation_groups)) {
      return;
    }

    this.selectedInventoryAllocationId = this.delivery.allocation_groups.find(ag => ag.sku_id === this.skuId)?.allocation_group_id;
  }

  get isItemInCart(): boolean {
    return !!this.orderItem?.id;
  }

  isFieldDisabled(outOfStock: boolean): boolean {
    if (this.disabled) {
      return true;
    }
    if (this.isItemInCart) {
      return false;
    }
    return outOfStock;
  }

}
